/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { cn } from '@/shared/ui/utils/tailwind';

type ResponsiveGutter = { base?: string | number; sm?: string | number; md?: string | number; lg?: string | number; xl?: string | number };

export type GridProps = React.HTMLAttributes<HTMLDivElement> & {
  gutter?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number | string | ResponsiveGutter;
  cols?: number | { base?: number; sm?: number; md?: number; lg?: number; xl?: number };
  columns?: number;
  justify?: string;
  sx?: React.CSSProperties;
  className?: string;
};

const GUTTER_MAP: Record<string, string> = {
  xs: '0.25rem',
  sm: '0.5rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
};

/**
 * Resolves a scalar gutter value to a CSS length string.
 *
 * @param gutter - Named size token, pixel number, or raw CSS value.
 * @returns CSS length string.
 */
function resolveScalarGutter(gutter: string | number | null | undefined): string {
  if (gutter == null) return '1rem';
  if (typeof gutter === 'number') return `${gutter}px`;
  return GUTTER_MAP[gutter] ?? gutter;
}

/**
 * Type guard that checks whether the gutter value is a responsive breakpoint object.
 *
 * @param gutter - Gutter value to test.
 * @returns True when gutter is a ResponsiveGutter object.
 */
function isResponsiveGutter(gutter: GridProps['gutter']): gutter is ResponsiveGutter {
  return typeof gutter === 'object' && gutter !== null && !Array.isArray(gutter);
}

let gridIdCounter = 0;

/**
 * Grid component using inline CSS Grid (12-column system).
 * All layout values are applied via inline styles so they work
 * regardless of Tailwind's static class scanning.
 */
const GridComponent: React.FC<GridProps> = ({
  gutter = 'md',
  cols,
  columns: columnsProp,
  justify,
  className,
  children,
  style,
  sx,
  ...props
}) => {
  const idRef = React.useRef(`gg-${++gridIdCounter}`);
  const resolvedStyle = { ...(sx ?? {}), ...(style ?? {}) };
  const columns = typeof cols === 'number' ? cols : (columnsProp ?? 12);
  const gridTemplateColumns = `repeat(${columns}, minmax(0, 1fr))`;

  if (isResponsiveGutter(gutter)) {
    const scopeClass = idRef.current;

    const baseGap = resolveScalarGutter(gutter.base);
    const rules: string[] = [];
    rules.push(`.${scopeClass} { display: grid; grid-template-columns: ${gridTemplateColumns}; gap: ${baseGap};${justify ? ` justify-items: ${justify};` : ''} }`);
    if (gutter.sm) rules.push(`@media (min-width: 640px) { .${scopeClass} { gap: ${resolveScalarGutter(gutter.sm)}; } }`);
    if (gutter.md) rules.push(`@media (min-width: 768px) { .${scopeClass} { gap: ${resolveScalarGutter(gutter.md)}; } }`);
    if (gutter.lg) rules.push(`@media (min-width: 1024px) { .${scopeClass} { gap: ${resolveScalarGutter(gutter.lg)}; } }`);
    if (gutter.xl) rules.push(`@media (min-width: 1280px) { .${scopeClass} { gap: ${resolveScalarGutter(gutter.xl)}; } }`);

    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: rules.join('\n') }} />
        <div
          className={cn(scopeClass, className)}
          style={resolvedStyle}
          {...props}
        >
          {children}
        </div>
      </>
    );
  }

  const gap = resolveScalarGutter(gutter);

  return (
    <div
      className={cn('grid', className)}
      style={{
        gridTemplateColumns,
        gap,
        ...(justify ? { justifyItems: justify } : {}),
        ...resolvedStyle,
      }}
      {...props}
    >
      {children}
    </div>
  );
};

export type GridColProps = React.HTMLAttributes<HTMLDivElement> & {
  span?: number | { base?: number; sm?: number; md?: number; lg?: number; xl?: number };
  className?: string;
};

/**
 * Responsive span values are handled via a generated inline <style> tag
 * scoped to this element via a stable CSS class. This avoids dynamic
 * Tailwind class names which don't generate CSS at build time.
 */
let colIdCounter = 0;

const GridCol: React.FC<GridColProps> = ({
  span,
  className,
  children,
  style,
  ...props
}) => {
  const idRef = React.useRef(`gc-${++colIdCounter}`);
  const scopeClass = idRef.current;

  if (span == null) {
    return (
      <div className={className} style={{ minWidth: 0, ...style }} {...props}>
        {children}
      </div>
    );
  }

  if (typeof span === 'number') {
    return (
      <div
        className={className}
        style={{ gridColumn: `span ${span} / span ${span}`, minWidth: 0, ...style }}
        {...props}
      >
        {children}
      </div>
    );
  }

  const base = span.base ?? 12;
  const rules: string[] = [];
  rules.push(`.${scopeClass} { grid-column: span ${base} / span ${base}; }`);
  if (span.sm) rules.push(`@media (min-width: 640px) { .${scopeClass} { grid-column: span ${span.sm} / span ${span.sm}; } }`);
  if (span.md) rules.push(`@media (min-width: 768px) { .${scopeClass} { grid-column: span ${span.md} / span ${span.md}; } }`);
  if (span.lg) rules.push(`@media (min-width: 1024px) { .${scopeClass} { grid-column: span ${span.lg} / span ${span.lg}; } }`);
  if (span.xl) rules.push(`@media (min-width: 1280px) { .${scopeClass} { grid-column: span ${span.xl} / span ${span.xl}; } }`);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: rules.join('\n') }} />
      <div
        className={cn(scopeClass, className)}
        style={{ minWidth: 0, ...style }}
        {...props}
      >
        {children}
      </div>
    </>
  );
};

const Grid = Object.assign(GridComponent, {
  Col: GridCol,
}) as typeof GridComponent & {
  Col: typeof GridCol;
};

export { Grid };
export { GridCol };
export default Grid;
